import WebGLTileLayer from 'ol/layer/WebGLTile';
import DataTile from 'ol/source/DataTile';
import TileGrid from 'ol/tilegrid/TileGrid';

import { fetchSimilarityFeatureTile } from '@utils/api/similarity';
import { rampStops } from './similarityStyle';

/**
 * Hover preview (proof of concept): the similarity map follows the cursor, computed on the GPU
 * from the orthophoto's patch features. The patch under the cursor, minus the mean of its
 * neighbours (as the server does for a one-patch example), is the prototype. A patch's
 * similarity is the dot product of its features with it. Hovering only updates style variables.
 */

// 31 feature components and the data band: 32 bands, 8 textures, safe on any GPU
const MAX_K = 31;
const TILE = 128;
// The colour range comes from every RANGE_STRIDE-th patch in both directions, scored in JS
const RANGE_STRIDE = 4;
// Further out than a view of this many feature tiles (about 0.5 MB each), there is no preview
const MAX_TILES_IN_VIEW = 30;

/**
 * @param {Object} params
 * @param {string} params.projectUuid
 * @param {string} [params.backbone] - Whose features the grid describes
 * @param {Object} params.grid - From getSimilarityFeatureGrid
 * @param {string[]} params.colors - The theme's `similarityColormap`
 * @param {number} params.opacity
 * @param {number} params.zIndex
 * @param {number[]} params.viewSize - The map's size in pixels, for the tile budget
 * @returns {{layer: WebGLTileLayer, maxResolution: number, showAt: function(number[], number[]):
 * void, hide: function(): void}} `showAt` takes the cursor and the view's extent
 */
export const createSimilarityHover = ({
	projectUuid,
	backbone,
	grid,
	colors,
	opacity,
	zIndex,
	viewSize,
}) => {
	const k = Math.min(MAX_K, grid.k);
	const bands = k + 1;
	const [x0, y0] = grid.origin;
	const [rows, cols] = grid.grid;
	const { patch_units: unit, scale } = grid;
	// Loaded tiles by `${tx},${ty}`, for the prototype and the colour range
	const tiles = new Map();

	// The coarsest resolution whose view needs at most MAX_TILES_IN_VIEW tiles, counting the
	// partial ones at the edges: (w·r/span + 1)(h·r/span + 1) = MAX_TILES_IN_VIEW, solved for r.
	// Above it the layer is off, so OpenLayers loads no tiles.
	const span = unit * TILE;
	const [width, height] = viewSize;
	const a = (width * height) / span ** 2;
	const b = (width + height) / span;
	const maxResolution = (-b + Math.sqrt(b * b + 4 * a * (MAX_TILES_IN_VIEW - 1))) / (2 * a);

	const source = new DataTile({
		tileGrid: new TileGrid({
			origin: [x0, y0],
			resolutions: [unit],
			tileSize: TILE,
			extent: [x0, y0 - rows * unit, x0 + cols * unit, y0],
		}),
		bandCount: bands,
		transition: 0,
		wrapX: false,
		loader: async (z, tx, ty, { signal }) => {
			const data = await fetchSimilarityFeatureTile({
				projectUuid,
				backbone,
				store: grid.store_id,
				k,
				tx,
				ty,
				signal,
			});
			if (data) tiles.set(`${tx},${ty}`, data);
			return data ?? new Uint8Array(TILE * TILE * bands);
		},
	});

	// The similarity on the colour range (0–1): bias + Σ band_i × w_i, where WebGL reads a band
	// as byte / 255. Weights and bias fold in the decoding, the prototype and the range.
	const names = Array.from({ length: k }, (_, i) => `w${i + 1}`);
	const value = [
		'+',
		['var', 'bias'],
		...names.map((name, i) => ['*', ['band', i + 1], ['var', name]]),
	];
	// The ramp from the threshold (0.5, the middle of the range) to the most similar
	const rampAt = ['/', ['-', value, 0.5], 0.5];
	// Always visible, so its tiles load; `show` hides the preview until there is a prototype
	const layer = new WebGLTileLayer({
		source,
		opacity,
		zIndex,
		maxResolution,
		style: {
			variables: Object.fromEntries(['show', 'bias', ...names].map(name => [name, 0])),
			color: [
				'case',
				['<', ['var', 'show'], 0.5],
				[0, 0, 0, 0],
				['<', ['band', bands], 0.5],
				[0, 0, 0, 0],
				['<', value, 0.5],
				[0, 0, 0, 0],
				['interpolate', ['linear'], rampAt, ...rampStops(colors).flat()],
			],
		},
	});

	// A patch's feature bytes, or null where nothing is loaded or there is no orthophoto
	const patchAt = (col, row) => {
		const data = tiles.get(`${Math.floor(col / TILE)},${Math.floor(row / TILE)}`);
		if (!data || col < 0 || row < 0) return null;
		const offset = ((row % TILE) * TILE + (col % TILE)) * bands;
		return data[offset + k] > 127 ? data.subarray(offset, offset + k) : null;
	};

	// The patch minus the mean of its valid neighbours, normalised
	const prototypeAt = (col, row) => {
		const centre = patchAt(col, row);
		if (!centre) return null;
		const mean = new Float32Array(k);
		let neighbours = 0;
		for (let dr = -1; dr <= 1; dr += 1) {
			for (let dc = -1; dc <= 1; dc += 1) {
				const patch = (dr || dc) && patchAt(col + dc, row + dr);
				if (!patch) continue;
				neighbours += 1;
				patch.forEach((byte, i) => (mean[i] += byte));
			}
		}
		const prototype = Float32Array.from(
			centre,
			(byte, i) => byte - (neighbours ? mean[i] / neighbours : 128)
		);
		const length = Math.hypot(...prototype) || 1;
		return prototype.map(component => component / length);
	};

	// lo = median, hi = maximum similarity of a sample of the patches in view
	const rangeFor = (prototype, [minX, minY, maxX, maxY]) => {
		const offset = 128 * prototype.reduce((sum, component) => sum + component, 0);
		const [left, right] = [Math.floor((minX - x0) / span), Math.floor((maxX - x0) / span)];
		const [top, bottom] = [Math.floor((y0 - maxY) / span), Math.floor((y0 - minY) / span)];
		const values = [];
		tiles.forEach((data, key) => {
			const [tx, ty] = key.split(',').map(Number);
			if (tx < left || tx > right || ty < top || ty > bottom) return;
			for (let row = 0; row < TILE; row += RANGE_STRIDE) {
				for (let col = 0; col < TILE; col += RANGE_STRIDE) {
					const start = (row * TILE + col) * bands;
					if (data[start + k] < 128) continue;
					let dot = 0;
					for (let i = 0; i < k; i += 1) dot += data[start + i] * prototype[i];
					values.push((dot - offset) * scale);
				}
			}
		});
		if (values.length === 0) return null;
		const sorted = Float32Array.from(values).sort();
		return { lo: sorted[sorted.length >> 1], hi: sorted[sorted.length - 1] };
	};

	let lastPatch = null;
	const showAt = ([x, y], viewExtent) => {
		const col = Math.floor((x - x0) / unit);
		const row = Math.floor((y0 - y) / unit);
		if (lastPatch === `${col},${row}`) return;
		lastPatch = `${col},${row}`;

		const prototype = prototypeAt(col, row);
		const range = prototype && rangeFor(prototype, viewExtent);
		if (!range || range.hi <= range.lo) {
			layer.updateStyleVariables({ show: 0 });
			return;
		}
		// similarity = Σ (255 × band_i − 128) × scale × p_i, put on the range lo…hi
		const span = range.hi - range.lo;
		const offset = 128 * scale * prototype.reduce((sum, component) => sum + component, 0);
		layer.updateStyleVariables({
			show: 1,
			bias: (-offset - range.lo) / span,
			...Object.fromEntries(
				names.map((name, i) => [name, (255 * scale * prototype[i]) / span])
			),
		});
	};

	const hide = () => {
		lastPatch = null;
		layer.updateStyleVariables({ show: 0 });
	};

	return { layer, maxResolution, showAt, hide };
};
